import { chain } from 'lodash';

import backendConnector from '../api/backendConnector';
import { SearchCriteriaTypes } from '../constants/SearchCriteria';
import StaticDataHttpService from '../api/StaticDataHttpService';
import UtilsService from './UtilsService';

export const defaultMaxItems = 20;

export default class SearchCriteriaService {
    static async getSuggestedCriteriaItems({
        criteriaConfig, currentValue, selectedCriteria, maxItems = defaultMaxItems, explicitSelected,
    }) {
        if (currentValue?.length === 1) {
            return [];
        }

        switch (criteriaConfig.type) {
            case SearchCriteriaTypes.static:
                return this.getSuggestionsStaticCriteria(criteriaConfig, currentValue, selectedCriteria, maxItems);
            case SearchCriteriaTypes.dynamic:
                return this.getSuggestionsForDynamicCriteria({
                    criteriaConfig, currentValue, selectedCriteria, maxItems, explicitSelected,
                });
            case SearchCriteriaTypes.semiDynamic:
                return this.getSuggestionsForSemiDynamicCriteria(criteriaConfig, currentValue, selectedCriteria, maxItems);
            case SearchCriteriaTypes.minMaxStatic:
            case SearchCriteriaTypes.freeText:
            default:
                return [];
        }
    }

    static async getSuggestionsStaticCriteria(criteriaConfig, currentValue, selectedCriteria, maxItems = defaultMaxItems) {
        if (!currentValue) {
            return [];
        }

        const staticData = await StaticDataHttpService.getStaticData();
        const allValues = staticData[criteriaConfig.field];
        const selectedValues = chain(selectedCriteria)
            .get('selected', [])
            .find({ field: criteriaConfig.field })
            .get('items')
            .map('text')
            .value();

        const suggestions = (allValues || [])
            .map(({
                text, name, printName, id,
            }) => ({ text: text || printName, value: name || id, id }))
            .filter(({ text }) => UtilsService.getIndexOf(text, currentValue) !== -1 && !selectedValues.includes(text))
            .sort((a, b) => (UtilsService.getIndexOf(a.text, currentValue) > UtilsService.getIndexOf(b.text, currentValue) ? 1 : -1));

        return (suggestions || []).slice(0, maxItems).map(({ text, value, ...rest }) => ({ text, value: value || text, ...rest }));
    }

    static async getSuggestionsForDynamicCriteria({
        criteriaConfig, currentValue, selectedCriteria, maxItemsToFetch = defaultMaxItems, explicitSelected,
    }) {
        const selectedValues = explicitSelected ? explicitSelected.items : chain(selectedCriteria)
            .get('selected', [])
            .find({ field: criteriaConfig.field })
            .get('items')
            .value();

        const values = await backendConnector.post(
            '/position/searchCriteria/suggestions',
            {
                fieldName: criteriaConfig.field,
                valueToSearch: currentValue,
                selectedValues,
                maxItemsToFetch,
                includeProblematicSkills: criteriaConfig.includeProblematics,
                positionFilter: selectedCriteria,
            },
        );

        return (values || []).map(({ value, isProblematic = false }) => ({ text: value, value, isProblematic }));
    }

    static async getSuggestionsForSemiDynamicCriteria(
        criteriaConfig,
        currentValue,
        selectedCriteria,
        maxItems = defaultMaxItems,
    ) {
        const selectedValues = chain(selectedCriteria)
            .get('selected', [])
            .find({ field: criteriaConfig.field })
            .get('items')
            .value();
        return backendConnector.post(
            '/position/searchCriteria/suggestions',
            {
                fieldName: criteriaConfig.field,
                valueToSearch: currentValue,
                selectedValues,
                maxItemsToFetch: maxItems,
                includeProblematicSkills: criteriaConfig.includeProblematics,
                positionFilter: selectedCriteria,
            },
        );
    }

    static async getNationwideLocations(uid) {
        return backendConnector.post('metadata/nationwideLocations', { uid });
    }

    static async getNationwideLocationsBulk(locations) {
        return backendConnector.post('metadata/nationwideLocationsBulk', { locations });
    }

    static getGdprLocations() {
        return backendConnector.get('metadata/gdprLocations');
    }

    static getTranslatedCountriesList() {
        return backendConnector.get('metadata/translatedCountriesList');
    }

    static getLocationCountryInfo({ locationName }) {
        return backendConnector.post('metadata/getLocationCountryInfo', { locationName });
    }

    static getLocationsSuggestions({ value, maxItems = 8 }) {
        const data = {
            currentField: {
                value,
                field: 'locations',
                type: 'Text',
                matchType: 'contains',
            },
            maxItems,
        };
        return backendConnector.post('metadata/semiDynamicLookups', data);
    }

    static searchIdentifiers({
        fieldName, filter, projection, sort, limit,
    }) {
        return backendConnector.post('identifiers/search', {
            fieldName, filter, projection, sort, limit,
        });
    }

    static getSearchFilterValues(data) {
        return backendConnector.post('search/getSearchFilterValues', data);
    }
}
